// Type-only namespace import — erased at compile time, so the runtime module still loads
// exclusively via the dynamic import() below (own chunk).
import type * as SignalRTypes from '@microsoft/signalr';
import type { HubConnection } from '@microsoft/signalr';

import { SIGNALR_VESR } from '../constants/global';
import { resolveVesrBearer } from './vesr-auth';

// Live support-chat client.
//
// A SIBLING of VesrIncidentLiveClient rather than a widening of it. That client's contract is
// deliberately narrow — one state room, two incident events, torn down when the Reports panel
// closes — and support needs the opposite lifetime: it lives as long as the session, because
// the FAB badge has to update while the chat panel is shut.
//
// Everything proven there is copied verbatim: dynamic import, accessTokenFactory, the retry
// ladder, room replay on reconnect, and the rule that a socket failure NEVER touches the
// session. A support outage must not sign a field agent out mid-election.
//
// ONE connection, MANY conversations. The session owns a single instance; each conversation
// engine subscribes to the events it cares about, filtered by thread key, and joins its own
// group. `on()` therefore ADDS a handler rather than replacing the previous one — the old
// one-handler-per-event map meant a second subscriber silently unregistered the first.

export type SupportConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

export type SupportEvent =
	| 'SupportMessageAdded'
	| 'SupportMessageEdited'
	| 'SupportMessageDeleted'
	| 'SupportMessagesRead'
	| 'SupportThreadUpdated'
	| 'SupportUnreadChanged'
	| 'UserTyping';

export type SupportHandler = (payload: unknown) => void;

type StatusListener = (status: SupportConnectionStatus) => void;

type SignalRModule = typeof SignalRTypes;

const RETRY_DELAYS_MS = [0, 2000, 5000, 10_000, 30_000];

/**
 * The thread a hub payload belongs to, when it says.
 *
 * <p>Every support event the server emits carries `threadKey` (the message DTOs, the delete,
 * read and typing payloads). `SupportUnreadChanged` is the one that may not: on older servers
 * it is a bare signal on the user's own group.</p>
 */
export const threadKeyOf = (payload: unknown): string | undefined => {
	if (!payload || typeof payload !== 'object') return undefined;
	// The hub's JSON protocol camel-cases by default; PascalCase is accepted in case a payload
	// is ever serialised without it. Hardening, not a contract.
	const { threadKey, ThreadKey, key: dtoKey } = payload as { threadKey?: unknown; ThreadKey?: unknown; key?: unknown };
	const key = threadKey ?? ThreadKey;
	if (typeof key === 'string') return key;
	// A peer thread SUMMARY (`SupportUnreadChanged` / `SupportThreadUpdated` on a peer thread)
	// carries its key as `key`. Only a peer key is accepted from that field: a support summary's
	// `key` was never a routing hint and older servers send that event bare on purpose.
	return typeof dtoKey === 'string' && dtoKey.startsWith('peer:') ? dtoKey : undefined;
};

export class SupportLiveClient {
	private signalR: SignalRModule | null = null;
	private connection: HubConnection | null = null;
	private statusCallback: StatusListener | null = null;
	private statusListeners = new Set<StatusListener>();
	/** Threads the server should have us in. Replayed on every (re)connect. */
	private joinedThreads = new Set<string>();
	/**
	 * How many subscribers want each thread.
	 *
	 * <p>The hub is told once on the first join and once on the last leave. Without this, a
	 * conversation panel and an inbox both holding the same key would have the panel's unmount
	 * drop the inbox out of the group.</p>
	 */
	private joinCounts = new Map<string, number>();
	/** Keys the hub has actually acknowledged, so a later subscriber retries a failed join. */
	private hubJoined = new Set<string>();
	private handlers = new Map<SupportEvent, Set<SupportHandler>>();
	private disposed = false;

	async connect(onStatusChange?: StatusListener): Promise<void> {
		this.disposed = false;
		this.statusCallback = onStatusChange ?? null;
		this.emitStatus('connecting');

		try {
			this.signalR = this.signalR ?? (await import('@microsoft/signalr'));
			this.buildConnection();
			await this.connection!.start();
			if (this.disposed) return;
			this.emitStatus('connected');
			await this.rejoinThreads();
		} catch (error) {
			// Warn, never throw. The chat degrades to its polling fallback and the outbox is
			// untouched — the agent can still compose and their messages still queue.
			console.warn('Support live connection failed:', error);
			this.emitStatus('disconnected');
		}
	}

	async disconnect(): Promise<void> {
		this.disposed = true;
		this.joinedThreads.clear();
		this.joinCounts.clear();
		this.hubJoined.clear();
		if (!this.connection) return;

		const connection = this.connection;
		this.connection = null;
		try {
			await connection.stop();
		} catch {
			// Already gone.
		}
	}

	/**
	 * Subscribe to an event. Returns the unsubscribe.
	 *
	 * <p>ADDS a handler. Two conversations listening for `SupportMessageAdded` both hear it and
	 * each decides for itself whether the payload is theirs — see {@link onThread}.</p>
	 */
	on(event: SupportEvent, handler: SupportHandler): () => void {
		let set = this.handlers.get(event);
		if (!set) {
			set = new Set();
			this.handlers.set(event, set);
		}
		set.add(handler);
		this.connection?.on(event, handler);
		return () => this.off(event, handler);
	}

	/** Remove one handler, or every handler for the event when none is given. */
	off(event: SupportEvent, handler?: SupportHandler): void {
		const set = this.handlers.get(event);
		if (!set) return;

		if (handler) {
			set.delete(handler);
			this.connection?.off(event, handler);
			if (set.size === 0) this.handlers.delete(event);
			return;
		}

		set.forEach((h) => this.connection?.off(event, h));
		this.handlers.delete(event);
	}

	/**
	 * Subscribe to an event for ONE thread.
	 *
	 * <p>Drops payloads that name another thread. A payload that names no thread at all passes
	 * through only for `SupportUnreadChanged`, which older servers emit as a bare signal on the
	 * user's group — every other support event has always carried its key, so an unkeyed one is
	 * malformed rather than legacy.</p>
	 */
	onThread(threadKey: string, event: SupportEvent, handler: SupportHandler): () => void {
		return this.on(event, (payload) => {
			const key = threadKeyOf(payload);
			if (key === undefined) {
				if (event === 'SupportUnreadChanged') handler(payload);
				return;
			}
			if (key === threadKey) handler(payload);
		});
	}

	/** Additional status listeners, beside the optional callback passed to connect(). */
	onStatus(listener: StatusListener): () => void {
		this.statusListeners.add(listener);
		return () => {
			this.statusListeners.delete(listener);
		};
	}

	get isConnected(): boolean {
		return !!this.connection && this.connection.state === this.signalR?.HubConnectionState.Connected;
	}

	/**
	 * Subscribe to a thread. Reference-counted — see {@link joinCounts}.
	 *
	 * The key is remembered even if the invoke fails, so the reconnect replay picks it up —
	 * a join that failed because the socket was mid-drop is exactly the one that must be
	 * retried when it comes back.
	 */
	async joinThread(threadKey: string): Promise<void> {
		const count = (this.joinCounts.get(threadKey) ?? 0) + 1;
		this.joinCounts.set(threadKey, count);
		this.joinedThreads.add(threadKey);

		// A second subscriber normally has nothing to do — unless the first one's invoke never
		// reached the hub (mid-drop, or not yet connected), in which case this is the retry.
		if (count > 1 && this.hubJoined.has(threadKey)) return;
		await this.invokeJoin(threadKey);
	}

	async leaveThread(threadKey: string): Promise<void> {
		const count = (this.joinCounts.get(threadKey) ?? 0) - 1;
		if (count > 0) {
			this.joinCounts.set(threadKey, count);
			return;
		}

		this.joinCounts.delete(threadKey);
		this.hubJoined.delete(threadKey);
		if (!this.joinedThreads.delete(threadKey)) return;
		await this.invokeIfConnected('LeaveSupportThread', threadKey);
	}

	/**
	 * "The app is alive and someone is looking."
	 *
	 * <p>Fire-and-forget, and deliberately NOT the same claim as a connected socket. A
	 * backgrounded app holds its socket for a long time; reporting that as reachable is what
	 * sends the desk into a void waiting for a reply that cannot come.</p>
	 */
	async heartbeat(): Promise<boolean> {
		return this.invokeIfConnected('SupportHeartbeat');
	}

	/** Typing is fire-and-forget: if it does not arrive, nothing is lost. */
	async sendTyping(threadKey: string, isTyping: boolean): Promise<void> {
		await this.invokeIfConnected('SendTypingIndicator', threadKey, isTyping);
	}

	private emitStatus(status: SupportConnectionStatus): void {
		this.statusCallback?.(status);
		this.statusListeners.forEach((listener) => listener(status));
	}

	private async rejoinThreads(): Promise<void> {
		// A new connection id means the server's groups start empty.
		this.hubJoined.clear();
		for (const threadKey of this.joinedThreads) {
			await this.invokeJoin(threadKey);
		}
	}

	private async invokeJoin(threadKey: string): Promise<void> {
		if (await this.invokeIfConnected('JoinSupportThread', threadKey)) this.hubJoined.add(threadKey);
	}

	/** Returns whether the call actually reached the server, so callers can fall back. */
	private async invokeIfConnected(method: string, ...args: unknown[]): Promise<boolean> {
		if (!this.connection || !this.signalR) return false;
		if (this.connection.state !== this.signalR.HubConnectionState.Connected) return false;

		try {
			await this.connection.invoke(method, ...args);
			return true;
		} catch (error) {
			// Server-side rejections land here — notably the hub's access check, which throws a
			// HubException for a thread the caller may not see. Logging and carrying on is
			// correct: the UI already shows nothing for a thread it cannot read.
			console.warn(`Support hub ${method} failed:`, error);
			return false;
		}
	}

	private buildConnection(): void {
		const signalR = this.signalR!;

		this.connection = new signalR.HubConnectionBuilder()
			.withUrl(SIGNALR_VESR, {
				// Async factory, re-invoked on every (re)connect — so a token refreshed since the
				// last attempt is picked up without rebuilding the client.
				accessTokenFactory: () => resolveVesrBearer(),
			})
			.configureLogging(signalR.LogLevel.Warning)
			.withAutomaticReconnect(RETRY_DELAYS_MS)
			.build();

		this.connection.onreconnecting(() => this.emitStatus('reconnecting'));

		this.connection.onreconnected(() => {
			this.emitStatus('connected');
			// Group membership does NOT survive a reconnect — SignalR gives you a new connection
			// id and the server's groups are keyed on it. Without this replay the socket looks
			// healthy and silently delivers nothing.
			void this.rejoinThreads();
		});

		this.connection.onclose(() => this.emitStatus('disconnected'));

		// Re-bind after every rebuild, so handlers registered before connect() survive.
		this.handlers.forEach((set, event) => set.forEach((handler) => this.connection?.on(event, handler)));
	}
}
